/**
 * The client's view of the session lifecycle, kept in module scope because it is shared by
 * every request rather than owned by any one of them.
 *
 * Two things live here:
 *
 * - Whether tecore-server is renewing the session. It renews an expired session without the
 *   client's involvement, so when it reports an ongoing renewal we keep a single leading
 *   request retrying and queue everything else behind it. That way we converge on one new
 *   session instead of every request racing to discover its own.
 * - Whether the session has ended for good, which puts the client in a non-recoverable
 *   state and must only be reported to the user once.
 */

const SESSION_RENEWAL_RETRY_MS = 2000;
const MAX_SESSION_RENEWAL_RETRIES = 3;

let sessionRenewalIsRunning = false;
let sessionRenewalRetries = 0;
let sessionRenewalQueue: (() => void)[] = [];
let sessionHasEnded = false;

export const isRenewalRunning = (): boolean => sessionRenewalIsRunning;

/** Holds a request until the renewal lands. */
export const queueUntilRenewed = (resume: () => void) => {
    sessionRenewalQueue.push(resume);
};

/** The renewal landed (or turned out not to be needed): let the queued requests go. */
export const flushRenewalQueue = () => {
    const queued = sessionRenewalQueue;
    clearRenewalQueue();
    queued.forEach((resume) => resume());
};

/** The renewal is over and the queued requests must not be sent. */
export const clearRenewalQueue = () => {
    sessionRenewalQueue = [];
    sessionRenewalIsRunning = false;
    sessionRenewalRetries = 0;
};

/**
 * Counts one attempt by the request leading the renewal and returns how long it should wait
 * before retrying, backing off a little further each time. Returns null once the retries are
 * exhausted, which means the renewal is never going to land.
 */
export const registerRenewalRetry = (): number | null => {
    sessionRenewalIsRunning = true;
    sessionRenewalRetries++;

    if (sessionRenewalRetries > MAX_SESSION_RENEWAL_RETRIES) {
        return null;
    }

    return SESSION_RENEWAL_RETRY_MS * sessionRenewalRetries;
};

export const hasSessionEnded = (): boolean => sessionHasEnded;

export const markSessionEnded = () => {
    sessionHasEnded = true;
};

/** For tests, which need a clean session between cases. */
export const resetSessionState = () => {
    clearRenewalQueue();
    sessionHasEnded = false;
};
